import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { tap, distinctUntilChanged, map, Observable, Subscription, pairwise, startWith, EMPTY, switchMap } from 'rxjs';

import { LayoutHeight } from 'rev-shared/webrtc/Layouts';
import { SecondMs } from 'rev-shared/date/Time.Constant';
import { StreamType } from 'rev-shared/webrtc/Contract';
import { WebRtcListenerConnectionService } from 'rev-shared/webrtc/WebRtcListenerConnection.Service';
import { escape } from 'rev-shared/util';
import { lazyHtml2canvas } from 'rev-shared/util/lazyHtml2Canvas';
import { truncateFirstName, truncateName } from 'rev-shared/util/nameUtil';

import { ThemeService } from 'rev-portal/branding/Theme.Service';
import { WebcastModel } from 'rev-portal/scheduledEvents/webcast/model/WebcastModel';

import { BannerTheme, BannerTiming, IProducerBannerOptions, IProducerLayout } from '../../Contract';
import { ProducerService } from '../../Producer.Service';
import { IVideoStreamProcessor, tryGetVideoProcessor } from './VideoStreamProcessor';

import styles from './VideoBanner.module.less';

const targetFont = 30;
const marginBottom = 0.05;
const marginSide = 0.025;

@Component({
	selector: 'banner-renderer',
	host: {
		'[class]': 'styles.bannerRenderer'
	},
	template: '',
	standalone: false
})
export class BannerRendererComponent implements OnInit, OnChanges, OnDestroy {
	@Input() public name: string;
	@Input() public banner: IProducerBannerOptions;
	@Input() public webcast: WebcastModel;

	public readonly styles = styles;

	private buffer: HTMLCanvasElement;
	private bufferValid: boolean;
	private sub: Subscription;
	private visible: boolean;
	private hideTimer: number;
	private processor: IVideoStreamProcessor;

	constructor(
		private readonly ProducerService: ProducerService,
		private readonly ThemeService: ThemeService,
		private readonly ListenerConnection: WebRtcListenerConnectionService
	) { }

	public ngOnInit(): void {
		this.ProducerService.setBannerRenderer(this);
		this.sub = this.ThemeService.brandingSettings$.subscribe(() => this.clearBuffer());

		this.sub.add(this.getBannerVisibility().subscribe({
			error: e => console.error('ProducerService getAutoBannerTimer: ', e)
		}));
	}

	public ngOnDestroy(): void {
		this.clearBuffer();
		this.ProducerService.setBannerRenderer(null);
		this.sub?.unsubscribe();
	}

	public ngOnChanges(): void {
		this.bufferValid = false;
	}

	private clearBuffer(): void {
		this.buffer = null;
		this.bufferValid = false;
	}

	public getStreamProcessor(stream: MediaStream): IVideoStreamProcessor {
		const processor = tryGetVideoProcessor(stream, this.renderer);
		this.processor = processor;
		return processor;
	}

	public readonly renderer = (): boolean => {
		const processor = this.processor;
		if(!this.visible) {
			return;
		}

		if(!this.bufferValid) {
			this.updateBuffer(processor.ctx, processor.width);
		}

		const img = this.buffer;
		if(!img) {
			return;
		}

		const top = (1 - marginBottom) * processor.height - img.height;
		const left = marginSide * processor.width;
		processor.ctx.imageSmoothingEnabled = true;
		processor.ctx.drawImage(img, left, top);
	};

	private updateBuffer(ctx: CanvasRenderingContext2D, w: number): void {

		const userId = this.webcast.currentUser.id;
		const video = [
			...this.webcast.producerOptions.onStageLayout.videos,
			...this.webcast.producerOptions.backstageLayout.videos
		].find(v => v.userId === userId && v.type === StreamType.Speaker);

		if(!video) {
			return;
		}

		const scaledFont = targetFont / video.rect.h * LayoutHeight;
		const text = sizeText(this.name, ctx, w, scaledFont);

		const style = this.getBannerStyle(scaledFont);
		const className = this.banner.theme === BannerTheme.Bubble ? 'bubble' : 'default';
		this.bufferValid = true;
		renderBanner(text, style, className)
			.then(image => {
				this.buffer = image;
				console.log('BANNER: banner canvas size: ', image.width, image.height);
			})
			.catch(e => {
				this.bufferValid = false;
				console.error('Error rendering banner: ', e);
			});
	}

	private getBannerStyle(fontSize: number): string {
		const fontUrl = new URL(this.ThemeService.brandingSettings.themeSettings.fontUri, document.baseURI);
		const accent = this.banner.accentColor || this.ThemeService.accentColor;

		return `
			@font-face {
				font-family: theme-font;
				src: url('${fontUrl}') format('woff');
			}

			#banner {
				display: block;
				font-family: theme-font;
				font-size: ${fontSize}px;
				border: 4px solid ${accent};
				border-radius: 0.2em;
				padding: 3px 20px 3px 0.5em;
				background: white;
				color: black;
				position: absolute;
				white-space: nowrap;
			}

			#banner.default .pip {
				position: absolute;
				top: 0;
				bottom: 0;
				left: 0;
				width: 0.3em;
				background: ${accent};
			}

			#banner.bubble {
				border-radius: 999px;
			}
		`;
	}

	private tryShowBanner(): void {
		this.hideBanner();
		const timing = this.webcast.producerOptions.banner.timing;
		if(!timing) {
			return;
		}

		this.visible = true;
		this.processor.enable();
		if(timing === BannerTiming.Auto10s) {
			this.hideTimer = setTimeout(() => this.stopBanner(), 10 * SecondMs) as any;
		}
	}


	private stopBanner(): void {
		this.hideBanner();
		this.processor?.disable();
	}

	private hideBanner(): void {
		clearTimeout(this.hideTimer);
		this.hideTimer = null;
		this.visible = false;
	}

	private getBannerVisibility(): Observable<any> {
		const userId = this.webcast.currentUser.id;

		const processor$ = this.ProducerService.producerStreams$.pipe(
			map(s => s.cameraStreamProcessor),
			distinctUntilChanged()
		);

		const isInLayout = (layout: IProducerLayout): boolean => {
			return this.ListenerConnection.presenters?.getLayoutStreams(layout, true)
				.some(s => s.presenter.id === userId);
		};

		const presenterBanners$ = this.webcast.webcast$.pipe(
			map(w => {
				const po = w.producerOptions;
				const presenters = this.ListenerConnection.presenters;
				if(!presenters){
					return [];
				}

				return [
					isInLayout(po.backstageLayout),
					isInLayout(po.onStageLayout),
					presenters.getLayoutSlotPresenter(po.onStageLayout.backgroundVideo)?.presenter.id === userId,
				];
			}),
			startWith([] as boolean[]),
			pairwise(),
			tap(([[wasBackstage, wasOnstage], [isBackstage, isOnstage, isOnstageBg]]) => {
				if(!isOnstage && !isBackstage || isOnstageBg) {
					this.stopBanner();
					return;
				}

				if(!wasOnstage && (isOnstage || isBackstage && !wasBackstage )) {
					this.tryShowBanner();
				}

			})
		);

		const broadcastTrigger$ = this.webcast.webcast$.pipe(
			map(w => w.broadcast.isActive),
			distinctUntilChanged(),
			switchMap(isBroadcasting => {
				if(isBroadcasting || !this.webcast.broadcast.hasBeenActive) {
					return processor$;
				}
				return EMPTY;
			}),
			switchMap(() => presenterBanners$)
		);

		return this.webcast.webcast$.pipe(
			map(w => w.producerOptions.banner.timing),
			distinctUntilChanged(),
			switchMap(timing => {
				if(!timing) {
					this.stopBanner();
					return EMPTY;
				}

				return broadcastTrigger$;
			})
		);
	}
}

function renderBanner(name: string, style: string, className: string): Promise<HTMLCanvasElement> {
	const html = `<html>
		<head>
			<style>${style}</style>
		</head>
		<body>
			<div id="banner" class="${className}">
				<span class="pip"></span>
				${escape(name)}
			</div>
		</body>
		</html>`;

	const iframe = document.createElement('iframe');
	iframe.className = styles.bannerRendererFrame;
	iframe.style.position = 'absolute';
	iframe.style.bottom = '0';
	iframe.style.height = '0px';
	iframe.style.width = '0px';

	return new Promise<HTMLCanvasElement>((resolve, reject) => {
		iframe.addEventListener('load', () => {
			const doc = iframe.contentDocument;
			doc.body.innerHTML = html;
			const el = doc.getElementById('banner');
			return resolve(lazyHtml2canvas(el, {
				backgroundColor: null,
				scale: 1
			}));
		});

		iframe.addEventListener('error', reject);
		document.body.appendChild(iframe);
	})
		.finally(() => {
			document.body.removeChild(iframe);
		});
}

/**
 * Truncates text to fit in the container
 */
function sizeText(txt: string, ctx: CanvasRenderingContext2D, containerWidth: number, baseFont: number): string {

	if(checkFontSize(txt)) {
		return txt;
	}

	txt = truncateFirstName(txt);
	if(checkFontSize(txt)) {
		return txt;
	}

	const step = 5;
	let txtTrunc: string;
	for(let l = txt.length - step; l > 0; l -= step) {
		txtTrunc = truncateName(txt, l);
		if(checkFontSize(txtTrunc)) {
			return txtTrunc;
		}
	}

	return txt;

	function checkFontSize(txt: string): boolean {
		txt = txt || ' ';
		const w = (1 - 2 * marginSide) * containerWidth - 50;
		const txtSize = measureTxt(txt, baseFont);
		return txtSize.width < w;
	}

	function measureTxt(txt: string, fontSize: number): TextMetrics {
		ctx.font = `${fontSize}px theme-font`;
		return ctx.measureText(txt);
	}
}
